// apps/admin/src/lib/notificationsI18n.ts
//
// Re-render a notification row's title/body at the ACTIVE UI locale via
// @printway/i18n's lookup(). Admin counterpart of
// `apps/merchant-v3/src/lib/notificationsI18n.ts`.
//
// Why: the BE persists title/body rendered at DISPATCH time, using the
// recipient's stored `User.language` (notification.worker.ts:170). When the
// admin flips locale in the topbar, those stored strings would stay frozen in
// the old language — the exact bug class of dbg-20260704-110206-3372. Rows that
// carry an `i18n_key` are re-rendered here so the bell follows the UI locale
// (CLAUDE.md i18n Tầng 1).
//
// Admin previously had no such helper (only merchant-v3 and vendor did), so
// bell rows never followed the locale switch — surfaced while verifying #2555.
//
// Fallback chain, in order: no key → stored strings; unknown key → stored
// strings; lookup throws (missing translation) → stored strings. The bell must
// never render blank because a catalog entry is absent.

import {
  isNotificationKey,
  lookup,
  type NotificationLocale,
} from '@printway/i18n';

import type { AdminNotification } from '../stores/useNotificationStore.js';

const SUPPORTED: readonly NotificationLocale[] = ['en', 'vi', 'fr'];

function normalizeLocale(lang: string | undefined): NotificationLocale {
  if (!lang) return 'en';
  const head = lang.toLowerCase().split('-')[0] ?? '';
  return (SUPPORTED as readonly string[]).includes(head)
    ? (head as NotificationLocale)
    : 'en';
}

export function translateNotification(
  row: Pick<AdminNotification, 'title' | 'body' | 'i18nKey' | 'i18nParams'>,
  lang: string | undefined,
): { title: string; body: string } {
  const key = row.i18nKey;
  if (!key || !isNotificationKey(key)) {
    return { title: row.title, body: row.body };
  }
  try {
    const tpl = lookup(
      key,
      normalizeLocale(lang),
      (row.i18nParams ?? undefined) as Record<string, string> | undefined,
    );
    return { title: tpl.title, body: tpl.body };
  } catch {
    return { title: row.title, body: row.body };
  }
}
